import { Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { useGetProjectLinkedModels } from '@/service-library/hooks/project-linked-models'
import { useGetPubModels } from '@/service-library/hooks/pub-models'
import SettingsPage from '@/components/shared/settings/SettingsPage'
import ProjectBreadcrumb from '../project-tables/ProjectBreadcrumb'
import { useProjectContext } from '../project-tables/ProjectProvider'
import AddModelButton from './AddModelButton'
import ModelCard from './ModelCard'

export default function ModelLibrary() {
  const { project } = useProjectContext()
  const { projectLinkedModels, isPending } = useGetProjectLinkedModels({
    filters: {
      project_id: project.id,
      fields__include: 'project_model,current_model_version,created_at',
      project_model__fields__include: 'project_model_type',
    },
  })

  const displayedProjectLinkedModels = projectLinkedModels
    .filter(
      (projectLinkedModel) =>
        projectLinkedModel.project_model?.project_model_type?.code !== 'OCR',
    )
    // Sort them by first added to last added
    .toSorted((a, b) =>
      new Date(a.created_at || '') > new Date(b.created_at || '') ? -1 : 0,
    )

  const modelIdsThatArePublished = displayedProjectLinkedModels
    .filter(({ project_model }) => project_model?.project_id !== project.id)
    .map(({ project_model_id }) => project_model_id)

  const { pubModels } = useGetPubModels({
    filters: {
      project_model_id__in: modelIdsThatArePublished.join(','),
      fields__only: 'id,name,project_model_id',
    },
    enabled: modelIdsThatArePublished.length > 0,
  })

  const projectModelNamesByLinkedModelId = displayedProjectLinkedModels.reduce<
    Record<string, string>
  >((acc, linkedModel) => {
    const modelName = modelIdsThatArePublished.includes(
      linkedModel.project_model_id,
    )
      ? pubModels.find(
          (model) => model.project_model_id === linkedModel.project_model_id,
        )?.name
      : linkedModel.project_model?.name

    acc[linkedModel.id] = modelName as string
    return acc
  }, {})

  return (
    <>
      <ProjectBreadcrumb label="Model Library" url="../model-library" />

      <SettingsPage
        title="Model Library"
        description="The models this project can use. A model has to be added here before the project can use it."
        width="lg"
        actions={
          <AddModelButton
            projectLinkedModels={projectLinkedModels}
            disabled={isPending}
          />
        }
      >
        {isPending && (
          <Typography variant="body2" color="text.secondary">
            Loading models...
          </Typography>
        )}
        {!isPending && displayedProjectLinkedModels.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No models in the library yet.
          </Typography>
        )}
        {displayedProjectLinkedModels.length > 0 && (
          <Grid container spacing={2}>
            {displayedProjectLinkedModels.map((projectLinkedModel) => (
              <Grid key={projectLinkedModel.id} size={{ xs: 12, md: 6, lg: 4 }}>
                <ModelCard
                  projectLinkedModel={projectLinkedModel}
                  modelName={
                    projectModelNamesByLinkedModelId[projectLinkedModel.id]
                  }
                  modelType={
                    projectLinkedModel.project_model?.project_model_type
                      ?.name as string
                  }
                />
              </Grid>
            ))}
          </Grid>
        )}
      </SettingsPage>
    </>
  )
}
